import React, { useState, useEffect } from 'react';
import { Lock, User, Eye, EyeOff, UserPlus, Smartphone, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { adminLogin, adminRegister, checkHasAdmin, verify2FA } from '../services/api';

const AdminLogin = ({ onLogin }) => {
  const [mode, setMode] = useState('login'); // 'login', 'register', or '2fa'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [hasAdmin, setHasAdmin] = useState(true);
  
  // 2FA state
  const [twoFACode, setTwoFACode] = useState('');
  const [pendingUserId, setPendingUserId] = useState(null);

  // Check if there's any admin user
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const result = await checkHasAdmin();
        setHasAdmin(result.has_admin);
        if (!result.has_admin) {
          setMode('register');
        }
      } catch (error) {
        console.error('Error checking admin:', error);
      } finally {
        setCheckingAdmin(false);
      }
    };
    checkAdmin();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await adminLogin(username, password);
      
      if (result.success) {
        // Check if 2FA is required
        if (result.requires_2fa) {
          setPendingUserId(result.user_id);
          setMode('2fa');
          setLoading(false);
          return;
        }
        
        // No 2FA - complete login
        localStorage.setItem('gelatti_admin_auth', 'true');
        localStorage.setItem('gelatti_admin_token', result.token);
        localStorage.setItem('gelatti_admin_user', JSON.stringify(result.user));
        localStorage.setItem('gelatti_admin_time', Date.now().toString());
        onLogin(true, result.user);
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Usuário ou senha incorretos');
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await verify2FA(pendingUserId, twoFACode);
      
      if (result.success) {
        localStorage.setItem('gelatti_admin_auth', 'true');
        localStorage.setItem('gelatti_admin_token', result.token);
        localStorage.setItem('gelatti_admin_user', JSON.stringify(result.user));
        localStorage.setItem('gelatti_admin_time', Date.now().toString());
        onLogin(true, result.user);
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Código inválido');
      setTwoFACode('');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    if (password.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      const result = await adminRegister(username, password, name);
      
      if (result.success) {
        // Auto login after register
        const loginResult = await adminLogin(username, password);
        if (loginResult.success) {
          localStorage.setItem('gelatti_admin_auth', 'true');
          localStorage.setItem('gelatti_admin_token', loginResult.token);
          localStorage.setItem('gelatti_admin_user', JSON.stringify(loginResult.user));
          localStorage.setItem('gelatti_admin_time', Date.now().toString());
          onLogin(true, loginResult.user);
        }
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao criar usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setMode('login');
    setPendingUserId(null);
    setTwoFACode('');
    setError('');
  };

  if (checkingAdmin) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-slate-100 to-teal-50 z-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // 2FA Verification Screen
  if (mode === '2fa') {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-slate-100 to-teal-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Smartphone className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">
              Verificação em 2 Etapas
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Digite o código do Google Authenticator
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handle2FAVerify} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Código de 6 dígitos
              </label>
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="pl-10 text-center text-2xl tracking-widest font-mono"
                  maxLength={6}
                  autoFocus
                  required
                  data-testid="2fa-code-input"
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || twoFACode.length !== 6}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl font-medium"
              data-testid="verify-2fa-btn"
            >
              {loading ? 'Verificando...' : 'Verificar'}
            </Button>

            <button
              type="button"
              onClick={handleBackToLogin}
              className="w-full text-slate-500 hover:text-slate-700 text-sm py-2"
            >
              Voltar para o login
            </button>
          </form>

          {/* Help */}
          <div className="mt-6 p-3 bg-slate-50 rounded-xl">
            <p className="text-xs text-slate-500 text-center">
              Abra o app Google Authenticator no seu celular e digite o código de 6 dígitos.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-100 to-teal-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            {mode === 'login' ? <Lock className="w-8 h-8 text-white" /> : <UserPlus className="w-8 h-8 text-white" />}
          </div>
          <h1 className="text-2xl font-bold text-slate-800">
            {mode === 'login' ? 'Painel Administrativo' : 'Criar Administrador'}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {mode === 'login' 
              ? 'Faça login para continuar' 
              : hasAdmin 
                ? 'Cadastre um novo administrador'
                : 'Crie o primeiro administrador do sistema'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={mode === 'login' ? handleLogin : handleRegister} className="space-y-4">
          {mode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Nome Completo
              </label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Seu nome"
                required
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Usuário
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Digite seu usuário"
                className="pl-10"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Senha
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Digite sua senha"
                className="pl-10 pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {mode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Confirmar Senha
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirme sua senha"
                  className="pl-10"
                  required
                />
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white py-3 rounded-xl font-medium"
          >
            {loading ? 'Aguarde...' : mode === 'login' ? 'Entrar' : 'Criar Conta'}
          </Button>
        </form>

        {/* Toggle mode - Only show if no admin exists */}
        {!hasAdmin && (
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500">
              Nenhum administrador cadastrado. Crie o primeiro acima.
            </p>
          </div>
        )}

        {/* Security note */}
        <div className="mt-6 p-3 bg-slate-50 rounded-xl">
          <p className="text-xs text-slate-500 text-center">
            Acesso restrito a administradores autorizados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
